/**
 * FinancialPendingInvoicesCard - Visão consolidada das faturas pendentes
 *
 * Componente compartilhado entre o Financeiro do tenant e o painel WL. Exibe
 * o total em aberto, a proporção por idade e uma tabela compacta por faixa.
 * A origem dos dados e a ação de detalhamento permanecem sob responsabilidade
 * de cada módulo, evitando qualquer mistura entre escopos de tenant e WL.
 *
 * @version 1.1.0
 * RELEASE:
 *   2026-10-03 v1.1.0 - Aceita acabamento opcional da composição sem alterar o painel WL.
 *   2026-09-12 v1.0.0 - Extração do padrão visual de aging do painel WL.
 */
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Identificadores estáveis das faixas de idade de uma fatura. */
export type FinancialAgingBucket =
  | 'future'
  | '1_30'
  | '31_60'
  | '61_90'
  | '91_120'
  | '120_plus';

/** Linha normalizada consumida pelo componente compartilhado. */
export interface FinancialPendingInvoiceRow {
  bucket: FinancialAgingBucket;
  label: string;
  count: number;
  total: number;
}

interface FinancialPendingInvoicesCardProps {
  /** Acabamento visual opcional fornecido pela tela consumidora. */
  className?: string;
  rows?: FinancialPendingInvoiceRow[];
  isLoading?: boolean;
  helpText: string;
  onRowClick?: (row: FinancialPendingInvoiceRow) => void;
}

/** Cores visuais das faixas, na mesma progressão usada no painel WL. */
const BUCKET_COLOR: Record<FinancialAgingBucket, string> = {
  future: 'bg-violet-400',
  '1_30': 'bg-yellow-200',
  '31_60': 'bg-yellow-400',
  '61_90': 'bg-orange-400',
  '91_120': 'bg-orange-600',
  '120_plus': 'bg-red-500',
};

/** Cores sólidas usadas nos marcadores da tabela. */
const BUCKET_DOT: Record<FinancialAgingBucket, string> = {
  future: 'bg-violet-500',
  '1_30': 'bg-yellow-300',
  '31_60': 'bg-yellow-500',
  '61_90': 'bg-orange-500',
  '91_120': 'bg-orange-700',
  '120_plus': 'bg-red-600',
};

/** Formata valores monetários sem abreviação para facilitar a auditoria. */
const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

export function FinancialPendingInvoicesCard({
  className,
  rows = [],
  isLoading,
  helpText,
  onRowClick,
}: FinancialPendingInvoicesCardProps) {
  if (isLoading) {
    return <Skeleton className="h-[380px] w-full" />;
  }

  const total = rows.reduce((sum, row) => sum + Number(row.total || 0), 0);

  return (
    <Card className={cn(className, 'h-full space-y-4 p-6')}>
      {/* Cabeçalho e ajuda contextual. */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>Faturas pendentes</span>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Ajuda: Faturas pendentes"
                    className="h-5 w-5"
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  {helpText}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums">{formatBRL(total)}</div>
        </div>
      </div>

      {/* Barra proporcional por faixa de idade. */}
      {total > 0 ? (
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
          {rows.map((row) => {
            const percentage = (Number(row.total || 0) / total) * 100;
            if (percentage <= 0) return null;

            return (
              <div
                key={row.bucket}
                className={cn('h-full', BUCKET_COLOR[row.bucket])}
                style={{ width: `${percentage}%` }}
                title={`${row.label}: ${percentage.toFixed(1)}%`}
              />
            );
          })}
        </div>
      ) : (
        <div className="h-2 w-full rounded-full bg-muted" />
      )}

      {/* Tabela compacta que mantém as seis faixas sempre visíveis. */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[420px] text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Idade da fatura</th>
              <th className="px-4 py-2 text-right font-medium">Faturas</th>
              <th className="px-4 py-2 text-right font-medium">Valor</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const hasValue = Number(row.total || 0) > 0 || Number(row.count || 0) > 0;
              const canOpen = hasValue && Boolean(onRowClick);

              return (
                <tr
                  key={row.bucket}
                  onClick={canOpen ? () => onRowClick?.(row) : undefined}
                  className={cn(
                    'border-t transition-colors',
                    index % 2 === 0 && 'bg-muted/20',
                    canOpen ? 'cursor-pointer hover:bg-primary/10' : 'cursor-default',
                    !hasValue && 'opacity-70',
                  )}
                  title={canOpen ? 'Clique para ver as faturas' : undefined}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', BUCKET_DOT[row.bucket])} />
                      <span>{row.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{row.count}</td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                    {formatBRL(Number(row.total || 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}